import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    CloverIpgSystemId,
    CloverIpgSystemIdKey,
    CardDetailsModel,
    defaultDemoIpgConfigByKey,
    defaultIpgConfigByKey,
    CloverGatewayEnvironment,
    type CloverIpgConfig,
    type CloverIpgConfigCustomization,
    CloverIpgConfigCustomizationSchema,
    type MerchantConfig,
    RpaModuleName,
    resolveDefaultCardDetailsModel,
} from '@pillar-portal/api-types';
import { sambaApi, sambaConfigApi } from '@/lib/pillarApi';
import { CloverReadinessCard } from '@/components/clover/CloverReadinessCard';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { useAuthStore } from '@/store/auth';
import {
    formatIpgPaymentFeatureCapabilityStateLabel,
    IpgPaymentFeatureCapabilityState,
    type IpgConfigEditorAdapter,
    type IpgPaymentFeatureCapabilityDisplayRule,
} from '../../ipg-config-editor-adapter';
import { CardDetailsModelControl } from '../CardDetailsModelControl';

export interface CloverOrganizationIpgConfigEditorState {
    readonly ipgSystemId: typeof CloverIpgSystemId;
    readonly ipgSystemIdKey: typeof CloverIpgSystemIdKey;
    readonly environment: CloverGatewayEnvironment;
    readonly portalUrl: string;
    readonly urlPattern: CloverIpgConfig['urlPattern'];
    readonly allowedWebsites: CloverIpgConfig['allowedWebsites'];
    readonly capabilityDisplayRules: readonly IpgPaymentFeatureCapabilityDisplayRule[];
    readonly cardDetailsModel: CardDetailsModel;
}

export interface CloverOrganizationIpgConfigEditorProps {
    readonly organizationUuid: string;
    readonly editorState: CloverOrganizationIpgConfigEditorState;
    readonly onChange: (nextState: CloverOrganizationIpgConfigEditorState) => void;
    readonly onPresetConfigChange: (ipgConfig: CloverIpgConfigCustomization) => void;
    readonly disabled: boolean;
    readonly productionPresetAvailable: boolean;
}

export interface CloverOrganizationIpgReadOnlySummaryProps {
    readonly organizationUuid: string;
    readonly organizationName: string | null;
    readonly editorState: CloverOrganizationIpgConfigEditorState;
    readonly providerAdministrationEnabled: boolean;
    /** INTERNAL_ADMIN/SUPERADMIN delegated setup, permitted without Clover workflow USE. */
    readonly delegatedProviderSetupEnabled: boolean;
}

const CloverDynamicReadinessFeatureIds: ReadonlySet<string> = new Set([
    'ecommerce_tokenization',
    'rest_pay_display',
    'terminal_payments',
    'terminal_tokenization',
    'heartbeat',
]);

export const CloverOrganizationIpgCapabilityDisplayRules: readonly IpgPaymentFeatureCapabilityDisplayRule[] =
    [
        {
            featureId: 'ecommerce_tokenization',
            label: 'Ecommerce tokenization',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip:
                'Clover Ecommerce tokenization can create payment tokens without storing PAN in the app.',
        },
        {
            featureId: 'rest_pay_display',
            label: 'REST Pay Display',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip:
                'Clover REST Pay Display can process terminal payments through supported devices.',
        },
        {
            featureId: 'terminal_payments',
            label: 'Terminal payments',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'Clover terminal payment APIs are configured.',
        },
        {
            featureId: 'terminal_tokenization',
            label: 'Terminal tokenization',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'Clover can tokenize cards from a terminal for stored-card payments.',
        },
        {
            featureId: 'heartbeat',
            label: 'Heartbeat',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'Clover merchant lookup can verify gateway readiness.',
        },
    ];

function cloneCloverOrganizationIpgConfig(config: CloverIpgConfig): CloverIpgConfig {
    return {
        ...config,
        allowedWebsites: config.allowedWebsites.map((allowedWebsite) => ({
            ...allowedWebsite,
            allowedUrlPatterns: [...allowedWebsite.allowedUrlPatterns],
            urlPatternsRules: allowedWebsite.urlPatternsRules.map((fetchPolicy) => ({
                action: fetchPolicy.action,
                urlPatterns: [...fetchPolicy.urlPatterns],
            })),
            iframeUrlPatterns: allowedWebsite.iframeUrlPatterns.map((iframePolicy) => ({
                action: iframePolicy.action,
                urlPatterns: [...iframePolicy.urlPatterns],
            })),
        })),
        rpaModules: [...config.rpaModules],
    };
}

function getCloverOrganizationIpgPortalUrl(config: CloverIpgConfig): string {
    return config.allowedWebsites[0]?.url ?? '';
}

function getProductionCloverOrganizationIpgConfig(): CloverIpgConfig {
    const config = defaultIpgConfigByKey[CloverIpgSystemIdKey];
    if (config.ipg_id !== CloverIpgSystemId) {
        throw new Error('Invalid Clover production IPG configuration.');
    }
    return config;
}

function getDemoCloverOrganizationIpgConfig(): CloverIpgConfig {
    const demoIpgConfig = defaultDemoIpgConfigByKey[CloverIpgSystemIdKey];
    if (demoIpgConfig === undefined || demoIpgConfig.ipg_id !== CloverIpgSystemId) {
        throw new Error('Missing Clover demo IPG configuration.');
    }
    return demoIpgConfig;
}

function getEffectiveCloverOrganizationIpgConfig(
    merchantConfig: MerchantConfig | undefined
): CloverIpgConfig {
    const persistedCloverConfig = merchantConfig?.ipg[CloverIpgSystemIdKey];
    if (persistedCloverConfig?.ipg_id === CloverIpgSystemId) {
        return cloneCloverOrganizationIpgConfig(persistedCloverConfig);
    }
    return cloneCloverOrganizationIpgConfig(getDemoCloverOrganizationIpgConfig());
}

export function buildCloverOrganizationIpgConfigEditorState(
    merchantConfig: MerchantConfig | undefined
): CloverOrganizationIpgConfigEditorState {
    const effectiveConfig = getEffectiveCloverOrganizationIpgConfig(merchantConfig);
    return {
        ipgSystemId: CloverIpgSystemId,
        ipgSystemIdKey: CloverIpgSystemIdKey,
        environment: effectiveConfig.environment,
        portalUrl: getCloverOrganizationIpgPortalUrl(effectiveConfig),
        urlPattern: effectiveConfig.urlPattern,
        allowedWebsites: effectiveConfig.allowedWebsites,
        capabilityDisplayRules: CloverOrganizationIpgCapabilityDisplayRules,
        cardDetailsModel:
            effectiveConfig.card_details_model ?? resolveDefaultCardDetailsModel(CloverIpgSystemId),
    };
}

function formatCloverOrganizationIpgEnvironmentLabel(
    environment: CloverGatewayEnvironment
): string {
    if (environment === CloverGatewayEnvironment.PRODUCTION) {
        return 'Production';
    }
    return 'Sandbox';
}

export function buildCloverOrganizationIpgConfigForSaveOrThrow(
    editorState: CloverOrganizationIpgConfigEditorState
): CloverIpgConfigCustomization {
    return CloverIpgConfigCustomizationSchema.parse({
        ipg_id: CloverIpgSystemId,
        environment: editorState.environment,
        card_details_model: editorState.cardDetailsModel,
    });
}

function CloverOrganizationIpgEnvironmentRadio(props: {
    readonly label: string;
    readonly checked: boolean;
    readonly disabled: boolean;
    readonly tooltip: string;
    readonly onChange: () => void;
}): ReactElement {
    return (
        <div className="flex items-center gap-2 text-sm">
            <span
                className="-m-[3px] inline-flex p-[3px]"
                title={props.tooltip}
                onClick={(event) => {
                    if (props.disabled || event.target !== event.currentTarget) {
                        return;
                    }
                    props.onChange();
                }}
            >
                <input
                    type="radio"
                    aria-label={props.label}
                    name="clover_ipg_environment"
                    checked={props.checked}
                    disabled={props.disabled}
                    title={props.tooltip}
                    className="h-4 w-4 accent-blue-600 disabled:opacity-50"
                    onChange={() => {
                        if (!props.disabled) {
                            props.onChange();
                        }
                    }}
                />
            </span>
            <span>{props.label}</span>
        </div>
    );
}

function buildCloverOrganizationIpgPresetState(
    editorState: CloverOrganizationIpgConfigEditorState,
    environment: CloverGatewayEnvironment
): CloverOrganizationIpgConfigEditorState {
    const presetConfig =
        environment === CloverGatewayEnvironment.SANDBOX
            ? getDemoCloverOrganizationIpgConfig()
            : getProductionCloverOrganizationIpgConfig();
    return {
        ...editorState,
        environment,
        portalUrl: getCloverOrganizationIpgPortalUrl(presetConfig),
        urlPattern: presetConfig.urlPattern,
        allowedWebsites: cloneCloverOrganizationIpgConfig(presetConfig).allowedWebsites,
    };
}

function getCloverEnvironmentRadioTooltip(input: {
    readonly environment: CloverGatewayEnvironment;
    readonly isPending: boolean;
    readonly isError: boolean;
    readonly isAvailable: boolean;
    readonly editorDisabled: boolean;
}): string {
    if (input.editorDisabled) {
        return 'Gateway configuration editing is currently unavailable.';
    }
    if (input.isPending) {
        return 'Checking Clover environment availability.';
    }
    if (input.isError) {
        return 'Clover environment availability could not be loaded.';
    }
    if (!input.isAvailable) {
        return `The ${formatCloverOrganizationIpgEnvironmentLabel(input.environment)} Clover app is not configured for this Anchorbase Portal environment.`;
    }
    return `Use the ${formatCloverOrganizationIpgEnvironmentLabel(input.environment)} Clover app.`;
}

export function CloverOrganizationIpgConfigEditor({
    organizationUuid,
    editorState,
    onChange,
    onPresetConfigChange,
    disabled,
    productionPresetAvailable,
}: CloverOrganizationIpgConfigEditorProps): ReactElement {
    const environmentAvailabilityQuery = useQuery({
        queryKey: ['sambaConfig', 'cloverRuntimeEnvironmentAvailability', organizationUuid],
        queryFn: () => sambaConfigApi.getCloverRuntimeEnvironmentAvailability(organizationUuid),
        retry: false,
    });

    return (
        <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Clover gateway configuration</p>
            <CardDetailsModelControl
                ipgSystemId={editorState.ipgSystemId}
                value={editorState.cardDetailsModel}
                onChange={(cardDetailsModel) => onChange({ ...editorState, cardDetailsModel })}
            />
            <div className="space-y-2">
                <p className="text-sm font-medium">Environment</p>
                <div className="flex flex-wrap gap-4">
                    {(
                        [
                            CloverGatewayEnvironment.PRODUCTION,
                            CloverGatewayEnvironment.SANDBOX,
                        ] as const
                    ).map((environment) => {
                        const isAvailable =
                            environmentAvailabilityQuery.data?.environments[environment] === true;
                        const isDisabled = disabled || !isAvailable;
                        const isProductionPresetUnavailable =
                            environment === CloverGatewayEnvironment.PRODUCTION &&
                            !productionPresetAvailable;
                        return (
                            <CloverOrganizationIpgEnvironmentRadio
                                key={environment}
                                label={formatCloverOrganizationIpgEnvironmentLabel(environment)}
                                checked={editorState.environment === environment}
                                disabled={isDisabled || isProductionPresetUnavailable}
                                tooltip={getCloverEnvironmentRadioTooltip({
                                    environment,
                                    isPending: environmentAvailabilityQuery.isPending,
                                    isError: environmentAvailabilityQuery.isError,
                                    isAvailable: isAvailable && !isProductionPresetUnavailable,
                                    editorDisabled: disabled,
                                })}
                                onChange={() => {
                                    const nextState = buildCloverOrganizationIpgPresetState(
                                        editorState,
                                        environment
                                    );
                                    onChange(nextState);
                                    onPresetConfigChange(
                                        buildCloverOrganizationIpgConfigForSaveOrThrow(nextState)
                                    );
                                }}
                            />
                        );
                    })}
                </div>
            </div>
            <p className="text-sm">Portal URL: {editorState.portalUrl}</p>
        </div>
    );
}

export function CloverOrganizationIpgReadOnlySummary({
    organizationUuid,
    organizationName,
    editorState,
    providerAdministrationEnabled,
    delegatedProviderSetupEnabled,
}: CloverOrganizationIpgReadOnlySummaryProps): ReactElement {
    const activeRoleAssignmentId = useAuthStore((state) => state.activeRoleAssignmentId);
    const authorizedModulesQuery = useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, ['authorized-rpa-modules']),
        queryFn: async () => await sambaApi.listModules(),
        enabled: providerAdministrationEnabled,
        staleTime: 30000,
    });
    const cloverModuleAuthorized =
        authorizedModulesQuery.data?.some((module) => module.name === RpaModuleName.CLOVER_IPG) ===
        true;
    return (
        <div className="space-y-2">
            <p className="text-sm font-medium">Clover gateway configuration</p>
            <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                <p>
                    Environment:{' '}
                    {formatCloverOrganizationIpgEnvironmentLabel(editorState.environment)}
                </p>
                <p>Portal URL: {editorState.portalUrl}</p>
                {editorState.capabilityDisplayRules
                    .filter((rule) => !CloverDynamicReadinessFeatureIds.has(rule.featureId))
                    .map((rule) => {
                        const isSupported =
                            rule.state === IpgPaymentFeatureCapabilityState.SUPPORTED;
                        return (
                            <p key={rule.featureId}>
                                {rule.label}:{' '}
                                <span
                                    title={rule.tooltip}
                                    aria-disabled={isSupported ? undefined : true}
                                    className={isSupported ? undefined : 'opacity-50'}
                                >
                                    {formatIpgPaymentFeatureCapabilityStateLabel(rule.state)}
                                </span>
                            </p>
                        );
                    })}
            </div>
            {providerAdministrationEnabled &&
                (cloverModuleAuthorized || delegatedProviderSetupEnabled) && (
                    <CloverReadinessCard
                        key={organizationUuid}
                        organizationName={organizationName}
                        organizationUuid={organizationUuid}
                        portalUrl={editorState.portalUrl}
                        renewalEnabled={cloverModuleAuthorized}
                        invalidationEnabled={cloverModuleAuthorized}
                        authorizationNavigationEnabled={true}
                        terminalSelectionEnabled={true}
                        readinessEnabled={cloverModuleAuthorized}
                        refreshIntervalEditingEnabled={cloverModuleAuthorized}
                    />
                )}
        </div>
    );
}

export const CloverOrganizationIpgConfigEditorAdapter: IpgConfigEditorAdapter<
    CloverOrganizationIpgConfigEditorState,
    CloverIpgConfigCustomization
> = {
    ipgSystemId: CloverIpgSystemId,
    ipgSystemIdKey: CloverIpgSystemIdKey,
    label: 'Clover',
    capabilities: {
        supportsEditableConfig: true,
        supportsReadOnlySummary: true,
        supportsDefaultCredentialPlaceholders: true,
    },
    createEditableState: ({ merchantConfig }) =>
        buildCloverOrganizationIpgConfigEditorState(merchantConfig),
    validateEditableState: ({ editorState }) => {
        buildCloverOrganizationIpgConfigForSaveOrThrow(editorState);
        return [];
    },
    buildSavePayload: ({ editorState }) =>
        buildCloverOrganizationIpgConfigForSaveOrThrow(editorState),
    renderEditable: ({
        organizationUuid,
        editorState,
        onChange,
        disabled,
        productionPresetAvailable,
    }) => (
        <CloverOrganizationIpgConfigEditor
            organizationUuid={organizationUuid}
            editorState={editorState}
            onChange={onChange}
            onPresetConfigChange={() => undefined}
            disabled={disabled}
            productionPresetAvailable={productionPresetAvailable}
        />
    ),
    renderReadOnlySummary: ({
        merchantConfig,
        organizationUuid,
        organizationName,
        providerAdministrationEnabled,
        delegatedProviderSetupEnabled,
    }) => (
        <CloverOrganizationIpgReadOnlySummary
            organizationName={organizationName}
            organizationUuid={organizationUuid}
            editorState={buildCloverOrganizationIpgConfigEditorState(merchantConfig)}
            providerAdministrationEnabled={providerAdministrationEnabled}
            delegatedProviderSetupEnabled={delegatedProviderSetupEnabled}
        />
    ),
};
